/**
 * RelationCellLabel — część B2 (domknięcie luki etykiety kartoteki).
 *
 * Renderuje czytelną etykietę rekordu kartoteki (pracownik/kontrahent/użytkownik)
 * w komórce listy oraz w trybie podglądu read-only rekordu — zamiast skrótu UUID.
 * B2 rozwiązuje etykietę tylko w pickerze formularza; tu domykamy widok listy + read.
 *
 * Kartoteki nie wspierają pobrania „po idkach", więc każdy id dociągamy przez endpoint
 * szczegółu adaptera (React Query cache'uje per id — ten sam rekord w wielu wierszach
 * = jeden request). Dopóki etykieta się ładuje (lub gdy fetch padnie) pokazujemy skrót
 * UUID jako fallback, żeby komórka nigdy nie była pusta.
 *
 * Używane wyłącznie przez `RelationFieldType.renderCell` dla pól wskazujących kartotekę
 * (`relationSystemModuleSlug`). Relacje do modułów custom zostają na dotychczasowym
 * renderze (osobny zakres).
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { SystemEntityAdapter } from '@/lib/system-entity-adapters';

function idFallback(id: string): string {
  return id.slice(0, 8) + '…';
}

/** Normalizuje wartość pola (UUID lub UUID[]) do listy niepustych id. */
function toIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  return typeof value === 'string' && value ? [value] : [];
}

export function RelationCellLabel({
  adapter,
  value,
}: {
  adapter: SystemEntityAdapter;
  value: unknown;
}) {
  const ids = toIds(value);

  const { data } = useQuery<Map<string, string>>({
    queryKey: ['relation-cell-label', adapter.slug, ...ids],
    queryFn: async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            // Adaptery z `resolveByIds` (dziś: WYŁĄCZNIE `documents`) nie mają endpointu
            // „szczegół po id" — rozwiązanie idzie przez batch `?ids=` (jeden element).
            if (adapter.resolveByIds) {
              const res = await api.get<Record<string, unknown>>(adapter.resolveByIds.path([id]));
              const rows =
                (res.data[adapter.resolveByIds.responseKey] as
                  | Record<string, unknown>[]
                  | undefined) ?? [];
              const raw = rows[0];
              return [id, raw ? adapter.getLabel(raw) : idFallback(id)] as const;
            }
            const detailPath = adapter.detailPath;
            const detailResponseKey = adapter.detailResponseKey;
            if (!detailPath || !detailResponseKey) return [id, idFallback(id)] as const;
            const res = await api.get<Record<string, unknown>>(detailPath(id));
            const raw = res.data[detailResponseKey] as Record<string, unknown> | undefined;
            return [id, raw ? adapter.getLabel(raw) : idFallback(id)] as const;
          } catch {
            return [id, idFallback(id)] as const;
          }
        }),
      );
      return new Map(entries);
    },
    enabled: ids.length > 0,
    staleTime: 60_000,
  });

  if (ids.length === 0) return <>—</>;
  return <>{ids.map((id) => data?.get(id) ?? idFallback(id)).join(', ')}</>;
}
